import React, { useState } from 'react';
import { useMutation } from 'react-query';
import axios from 'axios';
import { 
  BellIcon, 
  EnvelopeIcon, 
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const Settings = () => {
  // State for different settings sections
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    bookingUpdates: true,
    promotionalOffers: true,
    newsletterSubscription: true,
    travelTips: false
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [passwordErrors, setPasswordErrors] = useState({});
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState('');
  
  // Update notification settings mutation
  const updateNotificationsMutation = useMutation(
    async (settings) => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await axios.put(`${apiUrl}/user/settings/notifications`, settings, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 3000);
      },
      onError: (error) => {
        setUpdateError('Failed to update notification settings. Please try again.');
        setTimeout(() => setUpdateError(''), 3000);
      }
    }
  );
  
  // Change password mutation
  const changePasswordMutation = useMutation(
    async (passwordData) => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await axios.put(`${apiUrl}/user/settings/password`, passwordData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 3000);
      },
      onError: (error) => {
        if (error.response && error.response.data && error.response.data.errors) {
          setPasswordErrors(error.response.data.errors);
        } else {
          setUpdateError('Failed to update password. Please try again.');
          setTimeout(() => setUpdateError(''), 3000);
        }
      }
    }
  );
  
  // Handle notification toggle
  const handleNotificationToggle = (key) => {
    setNotificationSettings({
      ...notificationSettings,
      [key]: !notificationSettings[key]
    });
  };
  
  // Handle save notification settings
  const handleSaveNotificationSettings = () => {
    updateNotificationsMutation.mutate(notificationSettings);
  };
  
  // Handle password form change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({
      ...passwordForm,
      [name]: value
    });
    
    // Clear errors when typing
    if (passwordErrors[name]) {
      setPasswordErrors({
        ...passwordErrors,
        [name]: ''
      });
    }
  };
  
  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };
  
  // Handle password form submit
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    // Submit form
    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    });
  };
  
  return (
    <div className="space-y-8">
      {/* Notification Settings */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <BellIcon className="h-5 w-5 text-primary mr-2" />
            <h3 className="text-lg font-semibold">Notification Settings</h3>
          </div>
        </div>
        
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Communication Channels</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-gray-500">Receive updates via email</p>
                    </div>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="emailNotifications"
                      checked={notificationSettings.emailNotifications}
                      onChange={() => handleNotificationToggle('emailNotifications')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="emailNotifications"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        notificationSettings.emailNotifications ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="font-medium">SMS Notifications</p>
                      <p className="text-sm text-gray-500">Receive updates via text message</p>
                    </div>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="smsNotifications"
                      checked={notificationSettings.smsNotifications}
                      onChange={() => handleNotificationToggle('smsNotifications')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="smsNotifications"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        notificationSettings.smsNotifications ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Notification Types</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Booking Updates</p>
                    <p className="text-sm text-gray-500">Notifications about your tour bookings</p>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="bookingUpdates"
                      checked={notificationSettings.bookingUpdates}
                      onChange={() => handleNotificationToggle('bookingUpdates')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="bookingUpdates"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        notificationSettings.bookingUpdates ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Promotional Offers</p>
                    <p className="text-sm text-gray-500">Special deals and discounts</p>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="promotionalOffers"
                      checked={notificationSettings.promotionalOffers}
                      onChange={() => handleNotificationToggle('promotionalOffers')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="promotionalOffers"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        notificationSettings.promotionalOffers ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Newsletter Subscription</p>
                    <p className="text-sm text-gray-500">Monthly newsletter with travel updates</p>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="newsletterSubscription"
                      checked={notificationSettings.newsletterSubscription}
                      onChange={() => handleNotificationToggle('newsletterSubscription')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="newsletterSubscription"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        notificationSettings.newsletterSubscription ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Travel Tips</p>
                    <p className="text-sm text-gray-500">Tips and advice for your destinations</p>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="travelTips"
                      checked={notificationSettings.travelTips}
                      onChange={() => handleNotificationToggle('travelTips')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="travelTips"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        notificationSettings.travelTips ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={handleSaveNotificationSettings}
                disabled={updateNotificationsMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
              >
                {updateNotificationsMutation.isLoading ? 'Saving...' : 'Save Notification Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Password Settings */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <LockClosedIcon className="h-5 w-5 text-primary mr-2" />
            <h3 className="text-lg font-semibold">Password & Security</h3>
          </div>
        </div>
        
        <div className="p-6">
          <form onSubmit={handlePasswordSubmit}>
            <div className="space-y-4 max-w-md">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  className={`block w-full px-3 py-2 border ${
                    passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-sm text-red-500">{passwordErrors.currentPassword}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  className={`block w-full px-3 py-2 border ${
                    passwordErrors.newPassword ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-500">{passwordErrors.newPassword}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 8 characters and include a mix of letters, numbers, and symbols.
                </p>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  className={`block w-full px-3 py-2 border ${
                    passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{passwordErrors.confirmPassword}</p>
                )}
              </div>
              
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={changePasswordMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
                >
                  {changePasswordMutation.isLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* Account Deletion */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-red-600">Danger Zone</h3>
        </div>
        
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">Delete Account</h4>
            <p className="text-sm text-red-600 mb-4">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <button
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
      
      {/* Success/Error Messages */}
      {updateSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md shadow-md flex items-center">
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          <span>Settings updated successfully!</span>
        </div>
      )}
      
      {updateError && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md shadow-md flex items-center">
          <ExclamationCircleIcon className="h-5 w-5 mr-2" />
          <span>{updateError}</span>
        </div>
      )}
      
      {/* CSS for toggle switches */}
      <style jsx>{`
        .toggle-checkbox:checked {
          right: 0;
          border-color: #fff;
        }
        .toggle-label {
          transition: background-color 0.2s ease;
        }
        .toggle-checkbox {
          transition: all 0.2s ease;
          right: 6px;
        }
      `}</style>
    </div>
  );
};

export default Settings;
